import { type ReactNode, useEffect, useRef } from 'react'

import { Formik } from 'formik'

import { useDecodedAccessToken } from '@b-stock/fe-core/bstock-next/components'
import { type OrchestratedAuctionListingDetails } from '@b-stock/fe-core/bstock-next/queries'

import { useFirstDibsPurchase } from '@/buyer-portal/components/features/listing/hooks/useFirstDibsPurchase'
import { useActivePhase } from '@/buyer-portal/helpers/auctions/buyingFormat'
import { getUserMinBidAmountForAuction } from '@/helpers/getUserMinBidAmount'

import { useBidAmountSchema } from '../useBidAmountSchema'
import { useBidConfirmationModal } from './BidConfirmationModal/useBidConfirmationModal'
import { useBuyNowConfirmationModalContext } from './BuyNowConfirmationModal/useBuyNowConfirmationModal'

export type BidFormFormikValues = {
  amount: '' | number
  bidType?: 'quick_bid' | 'bid_now' | 'comparison_single_bid'
  quickBidButtonNumber?: number
}

export const BidFormFormikProvider = ({
  children,
  currency,
  formId,
  initialBidAmount,
  initialBidType,
  listing,
}: {
  children: ReactNode
  currency: string
  formId?: string
  initialBidAmount?: BidFormFormikValues['amount']
  initialBidType?: BidFormFormikValues['bidType']
  listing: OrchestratedAuctionListingDetails
}) => {
  const { auction, contract } = listing
  const { bidPrecision } = auction
  const userMinBidAmount = getUserMinBidAmountForAuction(auction)

  const { openModal } = useBidConfirmationModal()
  const { openModal: openBuyNowModal } = useBuyNowConfirmationModalContext()
  const personId = useDecodedAccessToken()?.personId
  const isBuyNowPhase = useActivePhase(listing) === 'BINO'
  const { earlyPrice } = useFirstDibsPurchase(listing)
  const buyNowPrice = earlyPrice ?? listing.auction.buyNow?.price ?? 0

  const hasAutoOpened = useRef(false)
  useEffect(() => {
    if (hasAutoOpened.current || !personId || !initialBidAmount) {
      return
    }
    // Buy Now purchases confirm through BuyNowConfirmationModal, a separate
    // modal from the regular bid flow's BidConfirmationModal below.
    if (isBuyNowPhase) {
      if (initialBidAmount >= buyNowPrice) {
        hasAutoOpened.current = true
        openBuyNowModal()
      }
      return
    }
    if (initialBidAmount >= userMinBidAmount) {
      hasAutoOpened.current = true
      openModal()
    }
  }, [
    initialBidAmount,
    buyNowPrice,
    isBuyNowPhase,
    openBuyNowModal,
    openModal,
    personId,
    userMinBidAmount,
  ])

  const initialValues: BidFormFormikValues = {
    amount: initialBidAmount ?? '',
    ...(initialBidType ? { bidType: initialBidType } : {}),
  }

  const validationSchema = useBidAmountSchema({
    bidPrecision,
    currency,
    isRelativeBid: contract?.bidBasis === 'RELATIVE',
    userMinBidAmount,
  })

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={openModal}
      validationSchema={validationSchema}
    >
      {(formikProps) => (
        <form id={formId} onSubmit={formikProps.handleSubmit}>
          {children}
        </form>
      )}
    </Formik>
  )
}
